import { useAnalytics } from '@order-up/analytics';
import { useAuth } from '@order-up/client/vite';
import { useEffect, useRef } from 'react';

/**
 * Bridges the centralized auth source-of-truth (`useAuth`) into the analytics
 * provider. Mounted INSIDE the `AnalyticsProvider` subtree (via ClientProviders)
 * so `useAnalytics()` resolves, and INSIDE the auth subscription so `useAuth()`
 * reflects live Firebase state.
 *
 * Behavior:
 *   - On a NON-anonymous user present → `identify(uid, traits)` + (if available)
 *     `group(restaurantId, 'restaurant')`.
 *   - On logout (user → null) → `reset()`.
 *
 * Anonymous guest sessions are intentionally NOT identified — checkout signs in
 * customers anonymously for cross-domain cart hand-off, and an anonymous uid is
 * not a stable identity worth attaching traits to. They simply remain
 * unidentified until/unless they upgrade to a real account.
 *
 * Calls fire only on a CHANGE of the relevant key (uid / restaurantId) so a
 * re-render storm doesn't spam the provider. `BaseAnalyticsProvider` swallows
 * errors and no-ops in dev, so no try/catch is needed here.
 */
export function AnalyticsIdentity({ restaurantId }: { restaurantId?: string }) {
    const analytics = useAnalytics();
    const { data } = useAuth();
    const authUser = data?.authUser ?? null;

    // The identity we last reported, so we only act on a genuine change.
    const lastIdentifiedUid = useRef<string | null>(null);
    const lastGroupedRestaurantId = useRef<string | null>(null);

    // identify on a real (non-anonymous) user; reset on logout.
    useEffect(() => {
        const isIdentifiable = !!authUser && !authUser.isAnonymous;

        if (isIdentifiable) {
            if (lastIdentifiedUid.current === authUser.uid) return; // unchanged
            lastIdentifiedUid.current = authUser.uid;
            analytics.identify(authUser.uid, {
                email: authUser.email ?? undefined,
                name: authUser.displayName ?? undefined,
            });
            return;
        }

        // No identifiable user. If we had previously identified someone, this is
        // a logout (or downgrade to anonymous) — reset the analytics identity.
        if (lastIdentifiedUid.current !== null) {
            lastIdentifiedUid.current = null;
            lastGroupedRestaurantId.current = null;
            analytics.reset();
        }
    }, [authUser, analytics]);

    // Associate the (identified) user with the restaurant group, once known.
    useEffect(() => {
        const isIdentifiable = !!authUser && !authUser.isAnonymous;
        if (!isIdentifiable || !restaurantId) return;
        if (lastGroupedRestaurantId.current === restaurantId) return; // unchanged
        lastGroupedRestaurantId.current = restaurantId;
        analytics.group(restaurantId, 'restaurant');
    }, [authUser, restaurantId, analytics]);

    return null;
}
